import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Every painting in frontend/tiles/, listed by scripts/build-tiles.js.
import TILE_POOL from '../tiles.generated.json';
import './TileAssembly.css';

// The homepage hero: sixteen small paintings float loose around the headline
// and, as the visitor scrolls, glide in one by one and lock into a single
// board - what a Mosaik actually is. The section is taller than the screen
// and the stage inside it is sticky, so scroll distance drives the assembly
// while the picture stays put.
//
// Every frame is computed from one number, the (smoothed) scroll progress,
// and applied as transforms/opacity only, so it stays cheap.

const COLS = 4;
const ROWS = 4;
const TILE_COUNT = COLS * ROWS;

// A fresh random board on every visit: sixteen different paintings from the
// pool. Only if the pool ever holds fewer than sixteen do paintings repeat.
function pickTiles(pool, count) {
  const shuffled = [...pool];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  if (shuffled.length === 0) return [];
  return Array.from({ length: count }, (_, i) => shuffled[i % shuffled.length]);
}

// Share of the scroll each tile spends travelling; tiles start staggered
// between ASSEMBLY_START and ASSEMBLY_START + ASSEMBLY_SPREAD.
const TRAVEL = 0.3;
const ASSEMBLY_START = 0.06;
const ASSEMBLY_SPREAD = 0.34;

const clamp = (v, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
const smoothstep = (a, b, v) => {
  const t = clamp((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const easeOutBack = (t) => {
  const c1 = 1.3;
  return 1 + (c1 + 1) * (t - 1) ** 3 + c1 * (t - 1) ** 2;
};

// Seeded so the scatter looks the same on every visit.
function mulberry32(seed) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// A rectangle traced like a quick brush stroke: slightly wobbly edges, soft
// corners, and a tail that overshoots where it started.
function brushRect(w, h, rand, amp) {
  const pts = [];
  const push = (x, y) => pts.push([x + (rand() - 0.5) * amp * 2, y + (rand() - 0.5) * amp * 2]);
  const edge = (x0, y0, x1, y1) => {
    const steps = Math.max(2, Math.round(Math.hypot(x1 - x0, y1 - y0) / 48));
    for (let k = 0; k < steps; k += 1) push(x0 + ((x1 - x0) * k) / steps, y0 + ((y1 - y0) * k) / steps);
  };
  edge(0, 0, w, 0);
  edge(w, 0, w, h);
  edge(w, h, 0, h);
  edge(0, h, 0, 0);
  pts.push([pts[0][0] + w * 0.06, pts[0][1] + (rand() - 0.5) * amp]);

  const f = (n) => n.toFixed(1);
  let d = `M${f(pts[0][0])},${f(pts[0][1])}`;
  for (let k = 1; k < pts.length - 1; k += 1) {
    const [x, y] = pts[k];
    const [nx, ny] = pts[k + 1];
    d += ` Q${f(x)},${f(y)} ${f((x + nx) / 2)},${f((y + ny) / 2)}`;
  }
  const [lx, ly] = pts[pts.length - 1];
  return `${d} L${f(lx)},${f(ly)}`;
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => window.matchMedia(REDUCED_MOTION).matches);
  useEffect(() => {
    const mq = window.matchMedia(REDUCED_MOTION);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function TileAssembly() {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();

  const sectionRef = useRef(null);
  const stageRef = useRef(null);
  const boardRef = useRef(null);
  const headRef = useRef(null);
  const hintRef = useRef(null);
  const captionRef = useRef(null);
  const framePathsRef = useRef([]);
  const tileRefs = useRef([]);
  const geometryRef = useRef(null);

  const [frame, setFrame] = useState(null);

  // Which paintings this visit - drawn once, so they don't reshuffle when
  // the language switches or anything else re-renders.
  const tiles = useMemo(() => pickTiles(TILE_POOL, TILE_COUNT), []);

  // Per-cell personality: where on the ring it waits, how tilted and small it
  // is while loose, when it lands, and how it bobs while it waits. Seeded, so
  // the choreography is always the same - only the paintings change.
  const params = useMemo(() => {
    const rand = mulberry32(11);
    const n = TILE_COUNT;

    const landingOrder = [...Array(n).keys()];
    for (let i = n - 1; i > 0; i -= 1) {
      const j = Math.floor(rand() * (i + 1));
      [landingOrder[i], landingOrder[j]] = [landingOrder[j], landingOrder[i]];
    }
    const rankOf = [];
    landingOrder.forEach((tile, rank) => {
      rankOf[tile] = rank;
    });

    // Each tile waits on the side of the ring it will fly in from, so the
    // assembly reads as everything drawing inward rather than criss-crossing.
    const direction = Array.from({ length: n }, (_, i) => {
      const c = i % COLS;
      const r = Math.floor(i / COLS);
      return Math.atan2(r - (ROWS - 1) / 2, c - (COLS - 1) / 2) + (rand() - 0.5) * 0.01;
    });
    const byDirection = [...Array(n).keys()].sort((a, b) => direction[a] - direction[b]);
    const slotOf = [];
    byDirection.forEach((tile, slot) => {
      slotOf[tile] = slot;
    });

    return Array.from({ length: n }, (_, i) => ({
      angle: -Math.PI + ((slotOf[i] + 0.5) / n) * Math.PI * 2 + (rand() - 0.5) * 0.35,
      radius: 0.8 + rand() * 0.22,
      rotation: (rand() - 0.5) * 50,
      scale: 0.52 + rand() * 0.3,
      start: ASSEMBLY_START + (rankOf[i] / n) * ASSEMBLY_SPREAD,
      introDelay: 250 + rankOf[i] * 65,
      phase: rand() * Math.PI * 2,
      speed: 0.45 + rand() * 0.45,
      z: Math.round(rand() * 10),
    }));
  }, []);

  // Where each tile sits on the ring relative to its home cell, plus the
  // hand-drawn frame around the finished board. Redone on any resize.
  useEffect(() => {
    const stage = stageRef.current;
    const board = boardRef.current;
    const section = sectionRef.current;
    if (!stage || !board || !section) return undefined;

    const measure = () => {
      const W = stage.clientWidth;
      const H = stage.clientHeight;
      // offset* ignore transforms, so the board's own tilt doesn't skew this.
      const size = board.offsetWidth;
      const bx = board.offsetLeft;
      const by = board.offsetTop;
      const gap = parseFloat(getComputedStyle(board).columnGap) || 0;
      const cell = (size - gap * (COLS - 1)) / COLS;
      const rx = W * 0.44;
      const ry = H * 0.41;

      const dx = [];
      const dy = [];
      params.forEach((p, i) => {
        const cx = bx + (i % COLS) * (cell + gap) + cell / 2;
        const cy = by + Math.floor(i / COLS) * (cell + gap) + cell / 2;
        dx[i] = W / 2 + Math.cos(p.angle) * rx * p.radius - cx;
        dy[i] = H / 2 + Math.sin(p.angle) * ry * p.radius - cy;
      });
      geometryRef.current = {
        dx,
        dy,
        top: section.getBoundingClientRect().top + window.scrollY,
        travel: Math.max(1, section.offsetHeight - H),
      };

      const pad = Math.round(size * 0.055);
      const span = size + pad * 2;
      const rand = mulberry32(Math.round(span));
      setFrame({
        pad,
        span,
        strokes: [brushRect(span, span, rand, span * 0.006), brushRect(span, span, rand, span * 0.011)],
      });
    };

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(stage);
    resizeObserver.observe(section);
    return () => resizeObserver.disconnect();
  }, [params, reduced]);

  // The animation loop - only runs while the hero is on screen.
  useEffect(() => {
    if (reduced) return undefined;
    const section = sectionRef.current;
    if (!section) return undefined;

    const mountedAt = performance.now();
    let smooth = null;
    let raf = 0;
    let visible = true;

    const tick = (now) => {
      raf = 0;
      const geo = geometryRef.current;
      if (geo) {
        const target = clamp((window.scrollY - geo.top) / geo.travel);
        // Ease toward the scroll position instead of snapping to it - this is
        // what makes wheel scrolling feel like gliding rather than ticking.
        smooth = smooth === null ? target : smooth + (target - smooth) * 0.09;
        if (Math.abs(target - smooth) < 0.0004) smooth = target;
        const p = smooth;
        const time = now / 1000;
        const sinceMount = now - mountedAt;

        tileRefs.current.forEach((el, i) => {
          if (!el) return;
          const tp = params[i];
          const landed = easeInOutCubic(clamp((p - tp.start) / TRAVEL));
          const loose = 1 - landed;
          const intro = clamp((sinceMount - tp.introDelay) / 900);
          const pop = easeOutBack(intro);

          const bobX = Math.sin(time * tp.speed + tp.phase) * 7 * loose;
          const bobY = Math.cos(time * tp.speed * 0.8 + tp.phase) * 10 * loose;
          const x = geo.dx[i] * loose + bobX;
          const y = geo.dy[i] * loose + bobY + (1 - intro) * 30;
          const rot = tp.rotation * loose + Math.sin(time * tp.speed * 0.6 + tp.phase) * 3 * loose;
          const scale = (tp.scale + (1 - tp.scale) * landed) * (0.55 + 0.45 * pop);

          el.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) rotate(${rot.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
          el.style.opacity = Math.min(1, intro * 1.6).toFixed(3);
          el.style.setProperty('--lift', loose.toFixed(3));
        });

        const head = headRef.current;
        if (head) {
          head.style.opacity = (1 - smoothstep(0.02, 0.2, p)).toFixed(3);
          head.style.transform = `translate3d(0, ${(-56 * smoothstep(0.02, 0.28, p)).toFixed(1)}px, 0)`;
        }
        if (hintRef.current) {
          hintRef.current.style.opacity = (1 - smoothstep(0, 0.05, p)).toFixed(3);
        }

        const board = boardRef.current;
        if (board) {
          // Tilted while it fills up, then straightened - hung on the wall.
          board.style.transform = `rotate(${(-3 * (1 - smoothstep(0.5, 0.82, p))).toFixed(3)}deg)`;
          board.style.setProperty('--board', smoothstep(0.45, 0.75, p).toFixed(3));
        }

        const drawn = smoothstep(0.7, 0.9, p);
        framePathsRef.current.forEach((path, k) => {
          if (!path) return;
          const amount = clamp(drawn * 1.08 - k * 0.08);
          path.style.strokeDashoffset = (1 - amount).toFixed(4);
          // An undrawn path still shows its round line-cap as a dot.
          path.style.visibility = amount > 0.001 ? 'visible' : 'hidden';
        });

        const caption = captionRef.current;
        if (caption) {
          const shown = smoothstep(0.8, 0.95, p);
          caption.style.opacity = shown.toFixed(3);
          caption.style.transform = `translate3d(0, ${((1 - shown) * 18).toFixed(1)}px, 0)`;
        }
      }
      if (visible) raf = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !raf) raf = requestAnimationFrame(tick);
    });
    observer.observe(section);
    raf = requestAnimationFrame(tick);

    return () => {
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [params, reduced]);

  return (
    <section ref={sectionRef} className={`ta ${reduced ? 'ta--static' : ''}`} aria-labelledby="ta-title">
      {/* Roughens the stamp's edges into something closer to a print. */}
      <svg className="ta-defs" aria-hidden="true" focusable="false">
        <filter id="ta-rough">
          <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="3" seed="4" />
          <feDisplacementMap in="SourceGraphic" scale="8" />
        </filter>
      </svg>

      <div ref={stageRef} className="ta-stage">
        <div ref={headRef} className="ta-head">
          <div className="ta-head-inner">
            <h1 id="ta-title" className="ta-title">{t('home.hero.title')}</h1>
            <p className="ta-lead">{t('home.hero.lead')}</p>
          </div>
        </div>

        <div ref={boardRef} className="ta-board" aria-hidden="true">
          {tiles.map((file, i) => (
            <div
              key={i}
              ref={(el) => {
                tileRefs.current[i] = el;
              }}
              className="ta-tile"
              style={{ zIndex: params[i].z }}
            >
              <img src={`/media/tiles/${file}`} alt="" draggable="false" decoding="async" />
            </div>
          ))}

          {frame && (
            <svg
              className="ta-frame"
              style={{ top: -frame.pad, left: -frame.pad, width: frame.span, height: frame.span }}
              viewBox={`0 0 ${frame.span} ${frame.span}`}
              preserveAspectRatio="none"
            >
              {frame.strokes.map((d, k) => (
                <path
                  key={k}
                  ref={(el) => {
                    framePathsRef.current[k] = el;
                  }}
                  className={`ta-frame-stroke ta-frame-stroke--${k}`}
                  d={d}
                  pathLength="1"
                />
              ))}
            </svg>
          )}
        </div>

        <p ref={captionRef} className="ta-caption">
          <strong>{t('home.hero.assembled', { n: TILE_COUNT })}</strong>
          <span>{t('home.hero.assembledSub')}</span>
        </p>

        <div ref={hintRef} className="ta-hint" aria-hidden="true">
          <span className="ta-hint-line" />
          {t('home.hero.scrollHint')}
        </div>

        <Link to="/order" className="ta-stamp">
          <span className="ta-stamp-bg" aria-hidden="true" />
          <span className="ta-stamp-side ta-stamp-side--left" aria-hidden="true">
            {t('home.hero.stampLeft')}
          </span>
          <span className="ta-stamp-main">
            {t('home.cta')}
            <span className="ta-stamp-arrow" aria-hidden="true">&rarr;</span>
          </span>
          <span className="ta-stamp-side ta-stamp-side--right" aria-hidden="true">
            {t('home.hero.stampRight')}
          </span>
        </Link>
      </div>
    </section>
  );
}

export default TileAssembly;
